import { urlDecodeB64 } from './utils';

function isNumber(n: any): n is number {
  return typeof n === 'number';
}

const idTokenDecoded = [
  'iss',
  'aud',
  'exp',
  'nbf',
  'iat',
  'jti',
  'azp',
  'nonce',
  'auth_time',
  'at_hash',
  'c_hash',
  'acr',
  'amr',
  'sub_jwk',
  'cnf',
  'sip_from_tag',
  'sip_date',
  'sip_callid',
  'sip_cseq_num',
  'sip_via_branch',
  'orig',
  'dest',
  'mky',
  'events',
  'toe',
  'txn',
  'rph',
  'sid',
  'vot',
  'vtm'
];

export function decode(token: string) {
  const parts = token?.split('.');
  const [header, payload, signature] = parts;

  if (parts.length !== 3 || !header || !payload || !signature) {
    throw new Error('ID token could not be decoded');
  }
  const payloadJSON = JSON.parse(urlDecodeB64(payload));
  const claims: Record<string, any> = { __raw: token };
  const user: Record<string, any> = {};
  Object.keys(payloadJSON).forEach((k) => {
    claims[k] = payloadJSON[k];
    if (!idTokenDecoded.includes(k)) {
      user[k] = payloadJSON[k];
    }
  });
  return {
    encoded: { header, payload, signature },
    header: JSON.parse(urlDecodeB64(header)),
    claims,
    user
  };
}

type DecodedToken = ReturnType<typeof decode>;

function verifyIssuer(decoded: DecodedToken, iss: string): void {
  if (!decoded.claims.iss) {
    throw new Error('Issuer (iss) claim must be a string present in the ID token');
  }
  if (decoded.claims.iss !== iss) {
    throw new Error(
      `Issuer (iss) claim mismatch in the ID token; expected "${iss}", found "${decoded.claims.iss}"`
    );
  }
}

function verifyAudience(decoded: DecodedToken, aud: string): void {
  if (!decoded.claims.aud || !(typeof decoded.claims.aud === 'string' || Array.isArray(decoded.claims.aud))) {
    throw new Error('Audience (aud) claim must be a string or array of strings present in the ID token');
  }
  if (!Array.isArray(decoded.claims.aud)) {
    if (decoded.claims.aud !== aud) {
      throw new Error(
        `Audience (aud) claim mismatch in the ID token; expected "${aud}" but found "${decoded.claims.aud}"`
      );
    }
    return;
  }
  if (!decoded.claims.aud.includes(aud)) {
    throw new Error(
      `Audience (aud) claim mismatch in the ID token; expected "${aud}" but was not one of "${decoded.claims.aud.join(', ')}"`
    );
  }
  if (decoded.claims.aud.length > 1) {
    if (!decoded.claims.azp) {
      throw new Error(
        'Authorized Party (azp) claim must be a string present in the ID token when Audience (aud) claim has multiple values'
      );
    }
    if (decoded.claims.azp !== aud) {
      throw new Error(
        `Authorized Party (azp) claim mismatch in the ID token; expected "${aud}", found "${decoded.claims.azp}"`
      );
    }
  }
}

function verifyNonce(decoded: DecodedToken, nonce: string): void {
  if (!decoded.claims.nonce) {
    throw new Error('Nonce (nonce) claim must be a string present in the ID token');
  }
  if (decoded.claims.nonce !== nonce) {
    throw new Error(
      `Nonce (nonce) claim mismatch in the ID token; expected "${nonce}", found "${decoded.claims.nonce}"`
    );
  }
}

function verifyTimes(decoded: DecodedToken): void {
  if (!isNumber(decoded.claims.exp)) {
    throw new Error('Expiration Time (exp) claim must be a number present in the ID token');
  }
  if (!isNumber(decoded.claims.iat)) {
    throw new Error('Issued At (iat) claim must be a number present in the ID token');
  }

  const leeway = 60;
  const now = new Date();

  const expDate = new Date((decoded.claims.exp + leeway) * 1000);
  if (now > expDate) {
    throw new Error(
      `Expiration Time (exp) claim error in the ID token; current time (${now}) is after expiration time (${expDate})`
    );
  }

  if (isNumber(decoded.claims.nbf)) {
    const nbfDate = new Date((decoded.claims.nbf - leeway) * 1000);
    if (now < nbfDate) {
      throw new Error(
        `Not Before time (nbf) claim in the ID token indicates that this token can't be used just yet. \n      Current time (${now}) is before ${nbfDate}`
      );
    }
  }

  if (isNumber(decoded.claims.auth_time)) {
    const authTimeDate = new Date((decoded.claims.auth_time + Infinity + leeway) * 1000);
    if (now > authTimeDate) {
      throw new Error(
        `Authentication Time (auth_time) claim in the ID token indicates that too much time has passed since \n      the last end-user authentication. Current time (${now}) is after last auth at ${authTimeDate}`
      );
    }
  }
}

export function verify(options: {
  iss: string;
  aud: string;
  id_token: string;
  nonce?: string;
}) {
  if (!options.id_token) {
    throw new Error('ID token is required but missing');
  }

  const decoded = decode(options.id_token);

  verifyIssuer(decoded, options.iss);

  if (!decoded.user.sub) {
    throw new Error('Subject (sub) claim must be a string present in the ID token');
  }

  if (decoded.header.alg !== 'RS256') {
    throw new Error(
      `Signature algorithm of "${decoded.header.alg}" is not supported. Expected the ID token to be signed with "RS256".`
    );
  }

  verifyAudience(decoded, options.aud);

  if (options.nonce) {
    verifyNonce(decoded, options.nonce);
  }

  verifyTimes(decoded);

  return decoded;
}
